import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { SEO } from "@/components/SEO";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";

const CATEGORIES = ["tees", "sets", "hoodies", "outerwear", "bottoms", "footwear", "watches", "fragrance", "accessories"];
const GENDERS = [
  { value: "men", label: "Men" },
  { value: "women", label: "Women" },
];
const COLLECTIONS = [
  { value: "heritage", label: "Héritage" },
  { value: "legacy", label: "Legacy" },
  { value: "genesis", label: "Genesis VII" },
];

const Filters = ({ filters, setFilters, priceRange, setPriceRange }) => {
  const toggle = (key, value) => {
    setFilters((prev) => {
      const list = prev[key];
      return { ...prev, [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] };
    });
  };

  return (
    <div className="space-y-10">
      <div>
        <p className="label-757 mb-5">Category</p>
        <div className="space-y-3.5">
          {CATEGORIES.map((c) => (
            <label key={c} className="flex items-center gap-3 cursor-pointer group">
              <Checkbox
                data-testid={`filter-category-${c}`}
                checked={filters.category.includes(c)}
                onCheckedChange={() => toggle("category", c)}
                className="border-white/30 data-[state=checked]:bg-[#0FB5A3] data-[state=checked]:border-[#0FB5A3] data-[state=checked]:text-black rounded-none"
              />
              <span className="text-sm text-white/60 capitalize group-hover:text-white transition-colors">{c}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="label-757 mb-5">Gender</p>
        <div className="space-y-3.5">
          {GENDERS.map((g) => (
            <label key={g.value} className="flex items-center gap-3 cursor-pointer group">
              <Checkbox
                data-testid={`filter-gender-${g.value}`}
                checked={filters.gender.includes(g.value)}
                onCheckedChange={() => toggle("gender", g.value)}
                className="border-white/30 data-[state=checked]:bg-[#0FB5A3] data-[state=checked]:border-[#0FB5A3] data-[state=checked]:text-black rounded-none"
              />
              <span className="text-sm text-white/60 group-hover:text-white transition-colors">{g.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="label-757 mb-5">Collection</p>
        <div className="space-y-3.5">
          {COLLECTIONS.map((c) => (
            <label key={c.value} className="flex items-center gap-3 cursor-pointer group">
              <Checkbox
                data-testid={`filter-collection-${c.value}`}
                checked={filters.collection.includes(c.value)}
                onCheckedChange={() => toggle("collection", c.value)}
                className="border-white/30 data-[state=checked]:bg-[#0FB5A3] data-[state=checked]:border-[#0FB5A3] data-[state=checked]:text-black rounded-none"
              />
              <span className="text-sm text-white/60 group-hover:text-white transition-colors">{c.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="label-757 mb-5">Price</p>
        <Slider
          data-testid="filter-price-slider"
          value={priceRange}
          onValueChange={setPriceRange}
          min={0}
          max={800}
          step={10}
          className="[&_[role=slider]]:bg-[#0FB5A3] [&_[role=slider]]:border-[#0FB5A3] [&_[role=slider]]:rounded-none"
        />
        <div className="flex justify-between mt-3 text-xs text-white/50">
          <span>${priceRange[0]}</span>
          <span>${priceRange[1]}</span>
        </div>
      </div>
    </div>
  );
};

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [priceRange, setPriceRange] = useState([0, 800]);
  const [filters, setFilters] = useState({
    category: searchParams.get("category") ? searchParams.get("category").split(",") : [],
    gender: searchParams.get("gender") ? searchParams.get("gender").split(",") : [],
    collection: searchParams.get("collection") ? [searchParams.get("collection")] : [],
  });

  const params = useMemo(() => {
    const p = { sort, limit: 60 };
    if (filters.category.length) p.category = filters.category.join(",");
    if (filters.gender.length) p.gender = filters.gender.join(",");
    if (filters.collection.length === 1) p.collection = filters.collection[0];
    if (search.trim()) p.search = search.trim();
    if (priceRange[0] > 0) p.min_price = priceRange[0];
    if (priceRange[1] < 800) p.max_price = priceRange[1];
    return p;
  }, [filters, sort, search, priceRange]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/products", { params })
        .then(({ data }) => setProducts(data))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [params]);

  useEffect(() => {
    const next = {};
    if (filters.gender.length) next.gender = filters.gender.join(",");
    if (filters.category.length) next.category = filters.category.join(",");
    if (filters.collection.length === 1) next.collection = filters.collection[0];
    if (search.trim()) next.search = search.trim();
    setSearchParams(next, { replace: true });
  }, [filters, search, setSearchParams]);

  const activeCount = filters.category.length + filters.gender.length + filters.collection.length;

  return (
    <div className="bg-black min-h-screen pt-32 pb-24">
      <SEO title="Shop" description="Shop the 7FIFTY7 collection — luxury hoodies, tees, outerwear, footwear, watches and accessories. Numbered editions built for legacy." />
      <div className="max-w-[1600px] mx-auto px-5 lg:px-10">
        <div className="mb-12">
          <p className="label-757 mb-3">The Full Collection</p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold text-white tracking-tight" data-testid="shop-heading">Shop</h1>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-4 mb-10 border-b border-white/10 pb-6">
          <div className="flex items-center gap-3 flex-1 min-w-[200px] border border-white/15 px-4 py-2.5">
            <Search size={15} className="text-white/40" />
            <input
              data-testid="shop-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search pieces..."
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/30"
            />
            {search && (
              <button onClick={() => setSearch("")} aria-label="Clear search" className="text-white/40 hover:text-white">
                <X size={14} />
              </button>
            )}
          </div>

          {/* Mobile filters */}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <button data-testid="mobile-filters-btn" className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs tracking-[0.2em] uppercase text-white/70">
                  <SlidersHorizontal size={14} /> Filters {activeCount > 0 && `(${activeCount})`}
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-black border-white/10 w-80 overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="font-display text-white tracking-[0.2em] uppercase text-sm text-left">Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-8">
                  <Filters filters={filters} setFilters={setFilters} priceRange={priceRange} setPriceRange={setPriceRange} />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="shop-sort-select" className="w-44 bg-transparent border-white/15 rounded-none text-xs tracking-[0.15em] uppercase text-white/70">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10 rounded-none text-white">
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-white/40 tracking-wider" data-testid="shop-results-count">
            {products.length} pieces
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-12">
          <aside className="hidden lg:block">
            <Filters filters={filters} setFilters={setFilters} priceRange={priceRange} setPriceRange={setPriceRange} />
          </aside>

          <div>
            {loading ? (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-[4/5] bg-[#0A0A0A] animate-pulse" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="py-24 text-center" data-testid="shop-empty-state">
                <p className="text-white/40 text-sm">No pieces match your filters.</p>
                <button
                  data-testid="shop-clear-filters-btn"
                  onClick={() => {
                    setFilters({ category: [], gender: [], collection: [] });
                    setSearch("");
                    setPriceRange([0, 800]);
                  }}
                  className="btn-757-outline mt-6"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-8" data-testid="shop-product-grid">
                {products.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
